import React, {
    useContext, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { isStandaloneApp } from 'app/utils/browser';
import Responsive, { Desktop } from 'app/components/Responsive';
import { DishAdLabel } from 'app/components/ui/Elements/DishAdLable/DishAdLabel';

import { DishImageShield } from 'app/components/ui/Elements/DishImageShield/DishImageShield';
import { useDishDetails } from 'app/components/MobileDishDetails';
import { isDesktop } from 'app/utils/resolution';
import { analyticService } from 'global/services';
import { useDishCustoms } from 'app/views/Hooks/useDishCustoms';
import { CustomIngredientsSelectDishRow } from 'app/views/Menu/Menu/components/CustomIngredientsSelectDishRow';
import DishPortionCounter from '../../DishButtons/DishPortionCounter';
import DishButton, { OrderButton } from '../../DishButtons/DishButton';

import { DishMediaContent } from '../DishMediaContent';
import { DishTitle } from '../DishTitile';
import { DishDescription } from '../DishDescription';

import clockIcon from '../../../../assets/images/clock.svg';
import blueClockIcon from '../../../../assets/images/blueClock.svg';
import { NYGiftContext } from '../../NYGiftContext';
import { ConfigContext } from '../../ConfigProvider';

export function DishLayoutDefault({
    metaLabel,
    index,
    id,
    parentId,
    isSoldOut,
    isWeightTimeHidden,
    isTimeHidden,
    partnersComment,
    portion,
    maxPortion,
    basketDishes,
    dishesById,
    basketDishesById,
    sortedDishes,
    originalPriceWithStep,
    discountPriceWithStep,
    price,
    dishCountStep,
    imageUrl,
    imageUrlDesktop,
    time,
    hour,
    minutes,
    weightValue,
    weight,
    isCentered,
    buttonsDisabled,
    canChangePortionsCount,
    areDetailsEnabled: areDetailsEnabledProp,
    buttonText,
    buttonTextAction,
    linkUrl,
    imageLinkUrl,
    typeformWidgetId,
    isNovelty,
    colors,
    nutrition,
    isNutritionEnabled,
    isLastDish,
    slides,
    joinDishTitlesinMobile,
    title,
    description,
    hourText,
    minutesText,
    weightText,
    timeText,
    trackAddToBasket,
    v2Layout,
    giftWrapId = undefined,
    pageId,
    tags,
    isDishRemovingDisabled,
}) {
    const isEasterBox = id === '4443';
    const isBlueColor = colors === 'blue';
    const isPinkColor = colors === 'pink';
    const isCuttlefishColor = colors === 'cuttlefish';
    const isMarchgiftsPage = pageId === 'marchgifts';
    const isSquidgamePage = pageId === 'squid-game';
    const isUpcoming = Boolean(typeformWidgetId);
    const hasLabel = isSoldOut || isUpcoming || isNovelty;

    const { openDishDetails } = useDishDetails();

    const config = useContext(ConfigContext);
    const isCustomizationEnabled = useMemo(
        () => config?.isCustomizationEnabled || false,
        [config],
    );

    const [selectedDishId, setSelectedDishId] = useState(id);
    const { ingredientsList, isDishCardWithCustomSelect } = useDishCustoms(id || '');

    const areDetailsEnabled = id === 'ym' ? false : areDetailsEnabledProp;

    const rootClasses = cn({
        ny__dish__container: true,
        'ny__dish__container-v2': v2Layout,
        'light-dish': colors !== 'dark' && colors !== 'blue' && colors !== 'pink',
        'dark-dish': colors === 'dark',
        'blue-dish': isBlueColor,
        'pink-dish': isPinkColor,
        'cuttlefish-dish': isCuttlefishColor,
        centered: isCentered,
        'ny__dish__container--last': isLastDish,
        'ny__dish__container--has-label': hasLabel,
        'ny__dish__container--has-label-sold-out': isSoldOut,
        'ny__dish__container--has-label-upcoming': !isSoldOut && isUpcoming,
        'ny__dish__container--has-label-novelty': !isSoldOut && isNovelty,
        'ny__dish__container--without-details': !areDetailsEnabled,
        [pageId]: true,
    });

    const childDish = useMemo(() => {
        if (selectedDishId === id) {
            return null;
        }

        if (dishesById) {
            return dishesById[selectedDishId];
        }

        return sortedDishes?.find((d) => d.id === selectedDishId) || null;
    }, [dishesById, sortedDishes, id, selectedDishId]);

    const basketChildDish = useMemo(() => {
        if (!childDish) {
            return undefined;
        }

        if (basketDishesById) {
            if (childDish.isSet) {
                return childDish.dishes
                    ?.map((childId) => basketDishesById[childId])
                    .find(Boolean);
            }

            return basketDishesById[childDish.id];
        }

        return basketDishes?.find((bd) => {
            if (childDish?.isSet) {
                return childDish?.dishes.includes(bd.id);
            }
            return bd.id === childDish?.id;
        });
    }, [basketDishes, basketDishesById, childDish]);

    const cardSlides = slides.filter((s) => s.placement === 'card');
    const containerProps = {
        'data-id': id,
        id: `dish_${id}`,
    };

    const textContainerProps = {};

    if (selectedDishId !== 'ym') {
        if (cardSlides.length === 0) {
            containerProps['data-toggle-details-id'] = selectedDishId;
        } else {
            textContainerProps['data-toggle-details-id'] = selectedDishId;
        }
    }

    /* FRNT-3400 NY2024 ПОДАРОЧНЫЙ КОСТЫЛЬ */
    const {
        isGiftItem,
        getItemsListById,
    } = useContext(NYGiftContext);

    const portionWithGift = useMemo(() => {
        if (isGiftItem(id)) {
            const { itemsList } = getItemsListById(id);
            return itemsList.length;
        }
        if (!childDish) return portion;

        return basketChildDish?.portion || 0;
    }, [portion, isGiftItem, id, getItemsListById, childDish, basketChildDish]);

    const squidgameDishClickTracker = () => {
        if (isSquidgamePage) {
            analyticService.push({
                eventName: 'Squid_game_Dish_Click',
                dish_id: id,
                dish_name: title,
            });
        }
    };

    const onNextIdSelected = (nextDishId) => {
        setSelectedDishId(nextDishId);
    };

    // const { originalPriceWithGift, discountPriceWithGift, priceWithGift } = useMemo(() => {
    //     if (giftWrapId) {
    //         const { isGiftItemSelected, name } = getItemsListById(id);
    //         if (name === 'cantucci') {
    //             return {
    //                 originalPriceWithGift: isGiftItemSelected ? originalPriceWithStep : Number(originalPriceWithStep) - 100,
    //                 discountPriceWithGift: isGiftItemSelected ? discountPriceWithStep : Number(discountPriceWithStep) - 100,
    //                 priceWithGift: isGiftItemSelected ? price : Number(price) - 100,
    //             };
    //         }
    //         return {
    //             originalPriceWithGift: !isGiftItemSelected ? originalPriceWithStep : Number(originalPriceWithStep) + 100,
    //             discountPriceWithGift: !isGiftItemSelected ? discountPriceWithStep : Number(discountPriceWithStep) + 100,
    //             priceWithGift: !isGiftItemSelected ? price : Number(price) + 100,
    //         };
    //     }

    //     return {
    //         originalPriceWithGift: originalPriceWithStep,
    //         discountPriceWithGift: discountPriceWithStep,
    //         priceWithGift: price,
    //     };
    // }, [getItemsListById, giftWrapId, id, discountPriceWithStep, originalPriceWithStep, price]);

    const tagsString = tags?.map((tag) => tag.id)?.join(',');

    const currentDishParams = useMemo(
        () => {
            if (childDish) {
                return {
                    ...childDish,
                    title: childDish?.title,
                    joinDishTitlesinMobile: childDish?.joinDishTitlesinMobile,
                    description: childDish?.description,
                    price: childDish?.price,
                    imageUrl: childDish?.imageUrl,
                    imageUrlDesktop: childDish?.imageUrlDesktop,
                };
            }
            return {
                title,
                joinDishTitlesinMobile,
                description,
                price,
                imageUrl,
                imageUrlDesktop,
            };
        },
        [
            childDish,
            title,
            joinDishTitlesinMobile,
            description,
            price,
            imageUrl,
            imageUrlDesktop,
        ],
    );

    return (
        <div
            className={rootClasses}
            {...containerProps}
            style={
                isCustomizationEnabled && id !== parentId
                    ? { position: 'absolute', visibility: 'hidden', left: 0 }
                    : undefined
            }
            role="none"
            onClick={(e) => {
                squidgameDishClickTracker();
                if (!isEasterBox) return;
                if (!isDesktop()) {
                    openDishDetails('4443', 'lp');
                    e.stopPropagation();
                }
            }}
        >
            <DishMediaContent
                id={selectedDishId}
                useLazyLoading={index === 0}
                alt={title[0]}
                imageUrl={currentDishParams.imageUrl}
                imageUrlDesktop={currentDishParams?.imageUrlDesktop}
                slides={cardSlides}
                additionalContent={(
                    <>
                        <DishAdLabel id={selectedDishId} />
                        <DishImageShield
                            dishId={selectedDishId}
                            isSoldOut={isSoldOut}
                            tagsString={tagsString}
                            realm="lp"
                        />
                        {canChangePortionsCount && <DishPortionCounter portion={portionWithGift} />}
                        {/* <div className="ny__dish__details-opener" {...textContainerProps} /> */}
                        {imageLinkUrl && (
                            <a
                                className="ny__dish__link-opener"
                                href={imageLinkUrl}
                                target={isStandaloneApp() ? '_self' : '_blank'}
                                rel="noopener noreferrer"
                            >
                                {imageLinkUrl}
                            </a>
                        )}
                    </>
                )}
            />

            <div className="ny__dish__bottom-container" {...textContainerProps}>
                <div>
                    <div className="ny__dish__desc-container">
                        <DishTitle
                            joinDishTitlesinMobile={currentDishParams?.joinDishTitlesinMobile}
                            title={currentDishParams?.title}
                            colors={colors}
                        />
                        <DishDescription
                            isEasterBox={isEasterBox}
                            areDetailsEnabled={areDetailsEnabled}
                            description={currentDishParams?.description}
                            partnersComment={partnersComment}
                            colors={colors}
                        />
                        {isNutritionEnabled && nutrition && (
                            <DishDescription
                                description={(
                                    <>
                                        На 1 готовую порцию:
                                        <Responsive mobile={<br />} desktop=" " />
                                        {weightValue}
                                        &nbsp;г |&nbsp;
                                        {Math.round((nutrition.calories.value * weightValue) / 100)}
                                        &nbsp;ккал
                                        | Б&nbsp;
                                        {Math.round((nutrition.protein.value * weightValue) / 100)}
                                        &nbsp;г
                                        | Ж&nbsp;
                                        {Math.round((nutrition.fat.value * weightValue) / 100)}
                                        &nbsp;г
                                        | У&nbsp;
                                        {Math.round((nutrition.carbohydrate.value * weightValue) / 100)}
                                        &nbsp;г
                                    </>
                                )}
                            />
                        )}
                        {isDishCardWithCustomSelect && isCustomizationEnabled && (
                            <div className="ny__dish__customization_select-wrapper">
                                <CustomIngredientsSelectDishRow
                                    id={id}
                                    selectedDishId={selectedDishId}
                                    onNextIdSelected={onNextIdSelected}
                                    optionsList={ingredientsList}
                                    onSubmitHandler={(arg) => { arg.onCompoleted(); }}
                                    environmentContext="lp"
                                    titleRenderField="replaceableIngredient"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div class={isCustomizationEnabled ? 'ny__dish__bottom-customization-container' : null}>
                    <div className="ny__dish__hr desktop-only" />

                    {!isMarchgiftsPage && ((weight || time) && !isWeightTimeHidden && !isUpcoming) ? (
                        <div
                            className={`ny__dish__info-container without-icon ${isCustomizationEnabled && 'with-customization'}`}

                        >
                            {weight ? (
                                <span className="ny__dish__weight">
                                    <span className="num">{weight}</span>
                                    &nbsp;
                                    {weightText}
                                </span>
                            ) : null}
                            {time && (!hour || !minutes) && !isTimeHidden ? (
                                <span className="ny__dish__cooking-time">
                                    {time !== ' ' && (
                                        <>
                                            <span className="num">{time}</span>
                                            &nbsp;
                                        </>
                                    )}
                                    {timeText}
                                </span>
                            ) : null}
                            {!isTimeHidden && (hour || minutes) ? (
                                <>
                                    <span className="ny__dish__cooking-time">
                                        {hour !== ' ' && (
                                            <>
                                                <span className="num">{hour}</span>
                                                &nbsp;
                                            </>
                                        )}
                                        {hourText}
                                        &nbsp;
                                    </span>

                                    <span className="ny__dish__cooking-time">
                                        {minutes !== ' ' && (
                                            <>
                                                <span className="num">{minutes}</span>
                                                &nbsp;
                                            </>
                                        )}
                                        {minutesText}
                                    </span>
                                </>
                            ) : null}
                        </div>
                    ) : (
                        <Desktop>
                            <div className="ny__dish__info-container ny__dish__info-container--stub">&nbsp;</div>
                        </Desktop>
                    )}

                    <div data-dish-button>
                        {(buttonText) ? (
                            <OrderButton
                                text={buttonText}
                                linkUrl={linkUrl}
                                isOpenDetailsHandler={isUpcoming}
                                withCustomizationSelect={isDishCardWithCustomSelect}
                            />
                        ) : (
                            canChangePortionsCount && !isSoldOut && !isUpcoming && (
                                <DishButton
                                    id={selectedDishId}
                                    giftWrapId={giftWrapId}
                                    originalPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : originalPriceWithStep}
                                    discountPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : discountPriceWithStep}
                                    price={isCustomizationEnabled ? currentDishParams?.price : price}
                                    dishCountStep={dishCountStep}
                                    portion={basketChildDish ? basketChildDish?.portion : portion}
                                    maxPortion={maxPortion}
                                    isInfoBlock
                                    buttonsDisabled={buttonsDisabled}
                                    onClickAddToBasket={trackAddToBasket}
                                    withCustomizationSelect={isDishCardWithCustomSelect}
                                    isDishRemovingDisabled={isDishRemovingDisabled}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

DishLayoutDefault.propTypes = {
    id: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    giftWrapId: PropTypes.string,
};
